import { JsonPropertiesMapper, ModelPropertiesMapper } from "jsona";
import _ from "lodash";

/** jsona 1.14+ only exports the package root; this matches its public mapper key. */
const RELATIONSHIP_NAMES_PROP = "relationshipNames";

const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  !!val && typeof val === "object" && val.constructor === Object;

type JsonaModel = Record<string, any>;
type JsonaAttributes = Record<string, any>;

type SwitchCaseModelMapperOptionsType = {
  switchAttributes?: boolean;
  switchRelationships?: boolean;
  switchType?: boolean;
};

type SwitchCaseJsonMapperOptionsType = {
  camelizeAttributes?: boolean;
  camelizeRelationships?: boolean;
  camelizeType?: boolean;
  camelizeMeta?: boolean;
};

const MODEL_TYPE_KEY = "__type";

const transformAttributes = (
  transformer: (attributes: JsonaAttributes) => JsonaAttributes,
  attributes: Record<string, any>
) => {
  if (!attributes) return attributes;

  if (typeof transformer === "function") {
    return transformer(attributes);
  }
  return attributes;
};

// Takes a camelCased object and converts to api request format
export class JsonApiModelMapper extends ModelPropertiesMapper {
  switchAttributes: boolean;
  switchRelationships: boolean;
  switchType: boolean;

  transformers: Record<string, any>;

  constructor(transformers = {}, options?: SwitchCaseModelMapperOptionsType) {
    super();

    const {
      switchAttributes = true,
      switchRelationships = true,
      switchType = true,
    } = options || {};

    this.switchAttributes = switchAttributes;
    this.switchRelationships = switchRelationships;
    this.switchType = switchType;

    this.transformers = transformers;
  }

  getId(model: JsonaModel) {
    const id = super.getId(model);
    return id ? String(id) : id;
  }

  getType(model: JsonaModel) {
    const type = model[MODEL_TYPE_KEY];

    if (!this.switchType || !type) {
      return type;
    }

    return this.convertFromCamelCaseString(type);
  }

  getAttributes(model: JsonaModel) {
    const exceptProps = ["id", MODEL_TYPE_KEY, RELATIONSHIP_NAMES_PROP];

    if (Array.isArray(model[RELATIONSHIP_NAMES_PROP])) {
      exceptProps.push(...model[RELATIONSHIP_NAMES_PROP]);
    } else if (model[RELATIONSHIP_NAMES_PROP]) {
      console.warn(
        `Can't getAttributes correctly, '%s' property of %s-%s model
                isn't array of relationship names`,
        RELATIONSHIP_NAMES_PROP,
        model.type,
        model.id,
        model[RELATIONSHIP_NAMES_PROP]
      );
    }

    let camelCasedAttributes = _.omit(model, exceptProps) as JsonaAttributes;

    camelCasedAttributes = transformAttributes(
      this.transformers[this.getType(model)],
      camelCasedAttributes
    );

    if (!this.switchAttributes || !camelCasedAttributes) {
      return camelCasedAttributes;
    }

    return this.convertFromCamelCase(camelCasedAttributes);
  }

  getRelationships(model: JsonaModel) {
    const camelCasedRelationships = super.getRelationships(model);

    if (!this.switchRelationships || !camelCasedRelationships) {
      return camelCasedRelationships;
    }

    return this.convertFromCamelCase(camelCasedRelationships);
  }

  private convertFromCamelCase(stuff: unknown) {
    if (Array.isArray(stuff)) {
      return stuff.map((item) => this.convertFromCamelCase(item));
    }

    if (isPlainObject(stuff)) {
      const converted = {};
      Object.entries(stuff).forEach(([propName, value]) => {
        // Preserve __type: lodash.kebabCase("__type") === "type", which breaks
        // getType() when relationship models are case-converted recursively.
        if (propName === MODEL_TYPE_KEY) {
          converted[propName] = value;
          return;
        }
        const kebabName = this.convertFromCamelCaseString(propName);
        converted[kebabName] = this.convertFromCamelCase(value);
      });
      return converted;
    }

    return stuff;
  }

  private convertFromCamelCaseString(camelCaseString: string) {
    return _.kebabCase(camelCaseString);
  }
}

// Receives a JSON:API response and converts it to a camelCased object
export class JsonApiJsonMapper extends JsonPropertiesMapper {
  camelizeAttributes: boolean;
  camelizeRelationships: boolean;
  camelizeType: boolean;
  camelizeMeta: boolean;

  transformers: Record<string, any>;

  constructor(transformers = {}, options?: SwitchCaseJsonMapperOptionsType) {
    super();

    const {
      camelizeAttributes = true,
      camelizeRelationships = true,
      camelizeType = true,
      camelizeMeta = false,
    } = options || {};

    this.camelizeAttributes = camelizeAttributes;
    this.camelizeRelationships = camelizeRelationships;
    this.camelizeType = camelizeType;
    this.camelizeMeta = camelizeMeta;

    this.transformers = transformers;
  }

  createModel(type: string): JsonaModel {
    if (!this.camelizeType) {
      return { [MODEL_TYPE_KEY]: type };
    }

    const camelizedType = this.convertToCamelCaseString(type);
    return { [MODEL_TYPE_KEY]: camelizedType };
  }

  setAttributes(model: JsonaModel, attributes: JsonaAttributes) {
    const attributesWithModelType = attributes;
    const transformedAttributes = transformAttributes(
      this.transformers[model[MODEL_TYPE_KEY]],
      this.convertToCamelCase(attributesWithModelType)
    );

    if (!this.camelizeAttributes) {
      return super.setAttributes(model, transformedAttributes);
    }

    Object.assign(model, transformedAttributes);
  }

  setMeta(model: JsonaModel, meta: JsonaAttributes) {
    if (!this.camelizeMeta) {
      return super.setMeta(model, meta);
    }

    model.meta = this.convertToCamelCase(meta);
  }

  setRelationships(model: JsonaModel, relationships: Record<string, any>) {
    // call super.setRelationships first, just for not to copy paste setRelationships logic
    super.setRelationships(model, relationships);

    if (!this.camelizeRelationships) {
      return;
    }

    // then change relationship names case if needed
    model[RELATIONSHIP_NAMES_PROP].forEach((kebabName, i) => {
      const camelName = this.convertToCamelCaseString(kebabName);
      if (camelName !== kebabName) {
        model[camelName] = model[kebabName];
        delete model[kebabName];
        model[RELATIONSHIP_NAMES_PROP][i] = camelName;
      }
    });
  }

  private convertToCamelCase(stuff: unknown) {
    if (Array.isArray(stuff)) {
      return stuff.map((item) => this.convertToCamelCase(item));
    }

    if (isPlainObject(stuff)) {
      const converted = {};
      Object.entries(stuff).forEach(([propName, value]) => {
        const camelName = this.convertToCamelCaseString(propName);
        converted[camelName] = this.convertToCamelCase(value);
      });
      return converted;
    }

    return stuff;
  }

  convertToCamelCaseString(notCamelCaseString: string) {
    return _.camelCase(notCamelCaseString);
  }
}
